import { useState, useMemo, useRef } from 'react'
import * as XLSX from 'xlsx'
import { supabase } from '../../supabaseClient'
import { C, st, formatDate } from '../../theme'
import { PageHeader, EmptyState, Modal, ScrollTable, Pagination, PAGE_SIZE } from '../../components/UI'
import QRPrintModal from '../../components/QRPrintModal'
import { useAppPrefs } from '../../AppPrefs'
import { makeT } from '../../i18n'

// ── Pure CSS bar chart ──
function ColBarChart({ data, color = C.accent, unit = '' }) {
  const max = Math.max(...data.map(d => d.v), 1)
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 90, padding: '0 4px' }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          <div style={{ fontSize: 10, color: C.muted, fontWeight: 700 }}>{d.v > 0 ? (unit === 'NT$' ? (d.v >= 1000 ? Math.round(d.v/1000)+'k' : d.v) : d.v) : ''}</div>
          <div style={{ width: '100%', height: Math.max(4, (d.v / max) * 62), background: `${color}cc`, borderRadius: '4px 4px 0 0', transition: 'height 0.4s' }} />
          <div style={{ fontSize: 9, color: C.muted, textAlign: 'center', lineHeight: 1.2, maxWidth: 36, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.k}</div>
        </div>
      ))}
    </div>
  )
}

// ── Rank bars ──
function RankBars({ data, color = C.accent, unit = '', max: maxProp }) {
  const max = maxProp || Math.max(...data.map(d => d.v), 1)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {data.map((d, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 18, fontSize: 11, color: C.muted, textAlign: 'right', fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontSize: 12 }}>
              <span style={{ color: C.text, fontWeight: 600, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.k}</span>
              <span style={{ color, fontWeight: 800 }}>{unit === 'NT$' ? `NT$${d.v.toLocaleString()}` : d.v}{unit && unit !== 'NT$' ? unit : ''}</span>
            </div>
            <div style={{ height: 7, background: C.border, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(d.v / max) * 100}%`, background: `${color}cc`, borderRadius: 4, transition: 'width 0.4s' }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Product Form (must be outside to prevent re-mount) ──
function ProductFormBody({ form, setForm, existingCategories }) {
  const { lang } = useAppPrefs()
  const _ = makeT(lang)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [showNewCat, setShowNewCat] = useState(false)
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
      {/* Basic Info */}
      <div style={{ gridColumn: 'span 2', padding: '10px 14px', background: C.accentDim, borderRadius: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.accent }}>基本資料 · Basic Info</div>
      </div>
      <div>
        <label style={st.label}>{_('productNameLabel')}</label>
        <input style={st.input} value={form.name} placeholder={_('productNamePh')} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
      </div>
      <div>
        <label style={st.label}>{_('productSku')}</label>
        <input style={st.input} value={form.sku} placeholder="SKU-001" onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} />
      </div>
      <div>
        <label style={st.label}>{_('productCategoryLabel')} · Category</label>
        {!showNewCat ? (
          <div style={{ display: 'flex', gap: 6 }}>
            <select style={{ ...st.input, flex: 1 }} value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
              <option value="">{_('productSelectCategory')}</option>
              {existingCategories.filter(c => c && c !== '未分類').map(c => <option key={c} value={c}>{c}</option>)}
              {/* 常用預設分類（尚未被任何商品使用時也提供快速選擇） */}
              {['半成品', 'NG品/可利用'].filter(c => !existingCategories.includes(c)).map(c => <option key={c} value={c}>{c}（建議）</option>)}
            </select>
            <button type="button" style={{ ...st.btn('default'), padding: '0 10px', fontSize: 11, minHeight: 44 }} onClick={() => setShowNewCat(true)}>{_('productAddCategory')}</button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 6 }}>
            <input style={{ ...st.input, flex: 1 }} value={form.category} placeholder={_('productNewCategoryHint')} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} autoFocus />
            <button type="button" style={{ ...st.btn('default'), padding: '0 10px', fontSize: 11, minHeight: 44 }} onClick={() => setShowNewCat(false)}>清單</button>
          </div>
        )}
        {(form.category === '半成品' || form.category === 'NG品/可利用') && (
          <div style={{ fontSize: 11, color: C.muted, marginTop: 5, padding: '6px 10px', background: '#fef9c3', borderRadius: 6 }}>
            💡 建議在商品名稱加註原始料號或來源，方便日後追蹤轉換來源（例：NG-A001 椅腳／可改製椅墊）
          </div>
        )}
      </div>
      <div>
        <label style={st.label}>{_('productUnit')} · Unit</label>
        <input style={st.input} value={form.unit} placeholder="個、箱、kg..." onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} />
      </div>

      {/* Stock + Pricing side by side */}
      <div style={{ gridColumn: 'span 2', padding: '10px 14px', background: '#f0fdf4', borderRadius: 10, marginTop: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.green }}>庫存與價格 · Stock & Pricing</div>
      </div>
      <div>
        <label style={st.label}>{_('productInitialStock')}</label>
        <input type="number" min="0" style={st.input} value={form.stock} placeholder="0" onChange={e => setForm(f => ({ ...f, stock: e.target.value }))} />
      </div>
      <div>
        <label style={st.label}>{_('productSafeStock')}</label>
        <input type="number" min="0" style={st.input} value={form.safe_stock} placeholder="0" onChange={e => setForm(f => ({ ...f, safe_stock: e.target.value }))} />
      </div>
      <div>
        <label style={st.label}>{_('productCostPrice')}</label>
        <input type="number" min="0" style={st.input} value={form.cost_price} placeholder="0" onChange={e => setForm(f => ({ ...f, cost_price: e.target.value }))} />
      </div>
      <div>
        <label style={st.label}>{_('productSellPrice')}</label>
        <input type="number" min="0" style={st.input} value={form.sell_price} placeholder="0" onChange={e => setForm(f => ({ ...f, sell_price: e.target.value }))} />
      </div>

      {/* Status */}
      <div>
        <label style={st.label}>狀態</label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 10, cursor: 'pointer', background: form.is_active ? C.greenBg : '#f1f5f9', border: `2px solid ${form.is_active ? C.greenBorder : C.border}`, minHeight: 52 }}>
          <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} style={{ width: 18, height: 18 }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: form.is_active ? C.green : C.muted }}>{form.is_active ? _('productActiveOpt') : _('productInactiveOpt')}</span>
        </label>
      </div>

      {/* Advanced section (collapsible) */}
      <div style={{ gridColumn: 'span 2' }}>
        <button type="button" onClick={() => setShowAdvanced(v => !v)} style={{ ...st.btn('default'), padding: '8px 14px', fontSize: 12, width: '100%', marginTop: 8 }}>
          {showAdvanced ? '▲ 收起進階設定' : '▼ 進階設定（規格、條碼、最小訂購量…）'}
        </button>
      </div>
      {showAdvanced && (
        <>
          <div>
            <label style={st.label}>{_('productBarcode')} · Barcode</label>
            <input style={st.input} value={form.barcode} placeholder="1234567890" onChange={e => setForm(f => ({ ...f, barcode: e.target.value }))} />
          </div>
          <div>
            <label style={st.label}>{_('productSpec')} · Spec</label>
            <input style={st.input} value={form.spec} placeholder={_('productSpecPh')} onChange={e => setForm(f => ({ ...f, spec: e.target.value }))} />
          </div>
          <div>
            <label style={st.label}>{_('productWeight')}</label>
            <input type="number" min="0" style={st.input} value={form.weight} placeholder="0.00" onChange={e => setForm(f => ({ ...f, weight: e.target.value }))} />
          </div>
          <div>
            <label style={st.label}>{_('productMinOrder')}</label>
            <input type="number" min="1" style={st.input} value={form.min_order_qty} onChange={e => setForm(f => ({ ...f, min_order_qty: e.target.value }))} />
          </div>
          <div>
            <label style={st.label}>前置時間（天）</label>
            <input type="number" min="0" style={st.input} value={form.lead_time} placeholder="0" onChange={e => setForm(f => ({ ...f, lead_time: e.target.value }))} />
          </div>
          <div>
            <label style={st.label}>圖片網址</label>
            <input style={st.input} value={form.image_url} placeholder="https://..." onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} />
          </div>
        </>
      )}
    </div>
  )
}

// ── Product Card ──
function ProductCard({ p, canEdit, canDelete, onEdit, onDelete, onToggleActive, onDetail, onQuickAdjust }) {
  const { lang } = useAppPrefs()
  const _ = makeT(lang)
  const isLow = p.stock <= p.safe_stock
  const margin = p.cost_price > 0 && p.sell_price > 0 ? Math.round(((p.sell_price - p.cost_price) / p.sell_price) * 100) : null
  return (
    <div style={{ background: '#fff', border: `2px solid ${isLow ? C.redBorder : C.border}`, borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 10, position: 'relative', transition: 'box-shadow 0.2s', boxShadow: isLow ? `0 0 0 3px ${C.red}22` : '0 1px 4px rgba(0,0,0,0.06)' }}>
      {/* Image / placeholder */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {p.image_url ? (
          <img src={p.image_url} alt={p.name} style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover', border: `1.5px solid ${C.border}`, flexShrink: 0 }} onError={e => { e.target.style.display = 'none' }} />
        ) : (
          <div style={{ width: 56, height: 56, borderRadius: 10, background: isLow ? C.redBg : C.accentDim, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>📦</div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.text, lineHeight: 1.3, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
          <div style={{ fontSize: 11, color: C.accent, fontFamily: 'monospace', marginBottom: 4 }}>{p.sku}</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {p.category && (
              <span style={{
                fontSize: 10, padding: '2px 7px', borderRadius: 6, fontWeight: 700,
                background: p.category === 'NG品/可利用' ? '#fef2f2' : p.category === '半成品' ? '#fff7ed' : '#ede9fe',
                color: p.category === 'NG品/可利用' ? '#dc2626' : p.category === '半成品' ? '#c2410c' : '#7c3aed',
              }}>
                {p.category === 'NG品/可利用' ? '⚠️ ' : p.category === '半成品' ? '🔧 ' : ''}{p.category}
              </span>
            )}
            {!p.is_active && <span style={{ fontSize: 10, padding: '2px 7px', background: '#f1f5f9', color: C.muted, borderRadius: 6 }}>⏸ 停用</span>}
            {isLow && <span style={{ fontSize: 10, padding: '2px 7px', background: C.redBg, color: C.red, borderRadius: 6, fontWeight: 700 }}>⚠ 低庫存</span>}
          </div>
        </div>
      </div>

      {/* Stock + price row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div style={{ background: isLow ? C.redBg : C.greenBg, border: `1.5px solid ${isLow ? C.redBorder : C.greenBorder}`, borderRadius: 10, padding: '8px 12px' }}>
          <div style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>{_('productCurrentStock')}</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: isLow ? C.red : C.green }}>{p.stock}<span style={{ fontSize: 12, fontWeight: 400 }}> {p.unit}</span></div>
          {p.safe_stock > 0 && <div style={{ fontSize: 10, color: C.muted }}>安全: {p.safe_stock}</div>}
        </div>
        <div style={{ background: '#f8fafc', border: `1.5px solid ${C.border}`, borderRadius: 10, padding: '8px 12px' }}>
          <div style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>價格</div>
          {p.cost_price > 0 && <div style={{ fontSize: 12, color: C.text, fontWeight: 700 }}>成本 NT${p.cost_price.toLocaleString()}</div>}
          {p.sell_price > 0 && <div style={{ fontSize: 12, color: C.accent, fontWeight: 700 }}>售價 NT${p.sell_price.toLocaleString()}</div>}
          {margin !== null && <div style={{ fontSize: 10, color: margin >= 30 ? C.green : C.yellow }}>毛利 {margin}%</div>}
          {!p.cost_price && !p.sell_price && <div style={{ fontSize: 12, color: C.muted }}>未設定</div>}
        </div>
      </div>

      {/* Quick adjust + actions */}
      {canEdit && onQuickAdjust && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f8fafc', borderRadius: 10, border: `1.5px solid ${C.border}` }}>
          <span style={{ fontSize: 11, color: C.muted, fontWeight: 600, flex: 1 }}>{_('productQuickAdjust')}</span>
          <button style={{ ...st.btn('danger'), padding: '4px 10px', fontSize: 13, minHeight: 32 }} onClick={() => onQuickAdjust(p, -1)}>－</button>
          <button style={{ ...st.btn('success'), padding: '4px 10px', fontSize: 13, minHeight: 32 }} onClick={() => onQuickAdjust(p, 1)}>＋</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button style={{ ...st.btn('default'), padding: '6px 10px', fontSize: 12, minHeight: 34, flex: 1 }} onClick={() => onDetail(p)}>{_('productDetail')}</button>
        {canEdit && <button style={{ ...st.btn('warning'), padding: '6px 10px', fontSize: 12, minHeight: 34, flex: 1 }} onClick={() => onEdit(p)}>編輯</button>}
        {canEdit && (
          <button style={{ ...st.btn(p.is_active !== false ? 'default' : 'success'), padding: '6px 10px', fontSize: 12, minHeight: 34 }} onClick={() => onToggleActive(p.id, p.is_active !== false)}>
            {p.is_active !== false ? _('productDeactivate') : _('productActivate')}
          </button>
        )}
        {canDelete && <button style={{ ...st.btn('danger'), padding: '6px 10px', fontSize: 12, minHeight: 34 }} onClick={() => onDelete(p.id, p.name)}>刪除</button>}
      </div>
    </div>
  )
}

export default function Products({ products, userProfile, onRefresh, showToast, addLog, enabledModules = [], isMobile = false }) {
  const { lang } = useAppPrefs()
  const _ = makeT(lang)
  const isAdmin = userProfile?.role === 'admin'
  const pos = userProfile?.position || 'user'
  const hasProductsPerm = enabledModules.includes('products')
  const hasWmsPerm = enabledModules.includes('wms')
  const canEdit = isAdmin || hasProductsPerm || ['factory_manager','senior_manager','sales','sales_assistant','section_chief'].includes(pos)
  const canDelete = isAdmin || enabledModules.includes('delete_products') || ['factory_manager','senior_manager'].includes(pos)
  const canAdjust = isAdmin || hasProductsPerm || hasWmsPerm || ['factory_manager','senior_manager','warehouse','section_chief'].includes(pos)

  const [activeTab, setActiveTab] = useState('list')  // 'list' | 'analytics'
  const [viewMode, setViewMode] = useState(isMobile ? 'card' : 'table')     // 'card' | 'table'; mobile defaults to card
  const [search, setSearch] = useState('')
  const [filterCat, setFilterCat] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [page, setPage] = useState(1)
  const [saving, setSaving] = useState(false)
  const [selected, setSelected] = useState([])
  const [editing, setEditing] = useState(null)
  const [showAdd, setShowAdd] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [showQRPrint, setShowQRPrint] = useState(false)
  const [importPreview, setImportPreview] = useState([])
  const [importErrors, setImportErrors] = useState([])
  const [importing, setImporting] = useState(false)
  const fileInputRef = useRef(null)
  const [detailProduct, setDetailProduct] = useState(null)
  const [deletedIds, setDeletedIds] = useState(new Set())
  const [deleteError, setDeleteError] = useState(null)  // string | null — shows modal instead of toast
  // Quick adjust modal
  const [quickAdjust, setQuickAdjust] = useState(null) // { product, delta }
  const [adjustNote, setAdjustNote] = useState('')

  const emptyForm = () => ({
    name: '', sku: '', category: '', unit: '', stock: 0, safe_stock: 0,
    spec: '', weight: '', cost_price: '', sell_price: '', barcode: '',
    min_order_qty: 1, lead_time: 0, image_url: '', is_active: true
  })
  const [form, setForm] = useState(emptyForm())

  // Derived data
  const existingCategories = useMemo(() => [...new Set(products.map(p => p.category).filter(Boolean))].sort(), [products])

  const displayProducts = useMemo(() => products.filter(p => !deletedIds.has(p.id)), [products, deletedIds])

  const filtered = useMemo(() => displayProducts.filter(p => {
    if (filterCat !== 'all') {
      const cat = p.category || '未分類'
      if (cat !== filterCat) return false
    }
    if (filterStatus === 'active' && p.is_active === false) return false
    if (filterStatus === 'inactive' && p.is_active !== false) return false
    if (filterStatus === 'low' && p.stock > p.safe_stock) return false
    if (search && !p.name?.includes(search) && !p.sku?.includes(search) && !p.barcode?.includes(search)) return false
    return true
  }), [displayProducts, filterCat, filterStatus, search])

  const CARD_PAGE = 12
  const pagedCard = useMemo(() => filtered.slice((page-1)*CARD_PAGE, page*CARD_PAGE), [filtered, page])
  const totalCardPages = useMemo(() => Math.ceil(filtered.length / CARD_PAGE), [filtered])

  const pagedTable = useMemo(() => filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE), [filtered, page])
  const totalTablePages = useMemo(() => Math.ceil(filtered.length / PAGE_SIZE), [filtered])

  const { activeCount, lowStockCount, categoryCount, totalValue } = useMemo(() => ({
    activeCount: displayProducts.filter(p => p.is_active !== false).length,
    lowStockCount: displayProducts.filter(p => p.stock <= p.safe_stock).length,
    categoryCount: new Set(displayProducts.map(p => p.category || '未分類')).size,
    totalValue: displayProducts.reduce((a, p) => a + (p.stock || 0) * (p.cost_price || 0), 0),
  }), [displayProducts])

  const hasFilter = search || filterCat !== 'all' || filterStatus !== 'all'

  // Analytics data
  const analytics = useMemo(() => {
    const cats = {}
    displayProducts.forEach(p => {
      const c = p.category || '未分類'
      if (!cats[c]) cats[c] = { count: 0, value: 0, lowStock: 0 }
      cats[c].count++
      cats[c].value += (p.stock || 0) * (p.cost_price || 0)
      if (p.stock <= p.safe_stock) cats[c].lowStock++
    })
    const catList = Object.entries(cats).sort((a, b) => b[1].value - a[1].value)
    const byValueTop = [...displayProducts]
      .filter(p => p.cost_price > 0)
      .sort((a, b) => (b.stock * b.cost_price) - (a.stock * a.cost_price))
      .slice(0, 8)
    const lowStockList = displayProducts
      .filter(p => p.stock <= p.safe_stock && p.is_active !== false)
      .sort((a, b) => (a.stock / Math.max(a.safe_stock, 1)) - (b.stock / Math.max(b.safe_stock, 1)))
      .slice(0, 10)
    const inactiveCount = displayProducts.filter(p => p.is_active === false).length
    const withPriceCount = displayProducts.filter(p => p.cost_price > 0 && p.sell_price > 0).length
    const avgMargin = withPriceCount > 0
      ? Math.round(displayProducts.filter(p => p.cost_price > 0 && p.sell_price > 0).reduce((a, p) => a + ((p.sell_price - p.cost_price) / p.sell_price * 100), 0) / withPriceCount)
      : null
    return { catList, byValueTop, lowStockList, inactiveCount, avgMargin, withPriceCount }
  }, [displayProducts])

  const handleSave = async () => {
    if (!form.name || !form.sku) { showToast('請填寫商品名稱和 SKU', 'error'); return }
    setSaving(true)
    // Only include known product columns — never spread id/created_at/updated_at from form
    const payload = {
      name: form.name,
      sku: form.sku,
      category: form.category || null,
      unit: form.unit || null,
      stock: parseInt(form.stock) || 0,
      safe_stock: parseInt(form.safe_stock) || 0,
      cost_price: parseFloat(form.cost_price) || 0,
      sell_price: parseFloat(form.sell_price) || 0,
      weight: parseFloat(form.weight) || null,
      min_order_qty: parseInt(form.min_order_qty) || 1,
      lead_time: parseInt(form.lead_time) || 0,
      spec: form.spec || null,
      barcode: form.barcode || null,
      image_url: form.image_url || null,
      is_active: form.is_active,
      updated_at: new Date().toISOString(),
    }
    if (editing) {
      const { error } = await supabase.from('products').update(payload).eq('id', editing.id)
      if (error) { showToast('更新失敗：' + error.message, 'error'); setSaving(false); return }
      await addLog('更新商品', `更新商品：${form.name}`)
      showToast('✓ 商品已更新')
      setEditing(null)
    } else {
      const cid = userProfile?.company_id
      let skuQ = supabase.from('products').select('id').eq('sku', form.sku)
      if (cid) skuQ = skuQ.eq('company_id', cid)
      const { data: existing } = await skuQ.maybeSingle()
      if (existing) { showToast(`SKU「${form.sku}」已存在`, 'error'); setSaving(false); return }
      const { error } = await supabase.from('products').insert({ ...payload, company_id: cid || null })
      if (error) { showToast('新增失敗：' + error.message, 'error'); setSaving(false); return }
      await addLog('新增商品', `新增商品：${form.name}`)
      showToast('✓ 商品已新增')
      setShowAdd(false)
    }
    setSaving(false)
    setForm(emptyForm())
    onRefresh()
  }

  const handleDelete = async (id, name) => {
    if (!window.confirm(`確定刪除商品「${name}」？\n⚠️ 如有關聯的入出庫紀錄，刪除可能失敗。`)) return
    setDeletedIds(prev => new Set([...prev, id]))
    try {
      // Use .select() so Supabase returns deleted rows — without it, silent RLS blocks
      // return { data: null, error: null } making us think delete succeeded
      const { data: deleted, error } = await supabase
        .from('products').delete().eq('id', id).select('id')

      if (error) {
        setDeletedIds(prev => { const n = new Set(prev); n.delete(id); return n })
        const isFK = error.message?.toLowerCase().includes('foreign key')
          || error.message?.toLowerCase().includes('violates')
          || error.code === '23503'
        setDeleteError(isFK
          ? `商品「${name}」有關聯的入出庫紀錄，無法直接刪除。\n\n建議改用「停用」功能，停用後商品不會出現在下拉選單中。`
          : `刪除失敗：${error.message || '未知錯誤'}`)
        return
      }

      // deleted is empty → RLS silently blocked or no matching row
      if (!deleted || deleted.length === 0) {
        setDeletedIds(prev => { const n = new Set(prev); n.delete(id); return n })
        setDeleteError(`刪除失敗：沒有足夠的權限刪除此商品，或商品不存在。\n\n請確認您的帳號有刪除權限，或聯絡系統管理員。`)
        return
      }

      try { await addLog('刪除商品', `刪除商品：${name}`) } catch (_) {}
      showToast('✓ 已刪除')
      setSelected(prev => prev.filter(s => s !== id))
      onRefresh()
    } catch (err) {
      setDeletedIds(prev => { const n = new Set(prev); n.delete(id); return n })
      showToast('刪除失敗：' + (err.message || '未知錯誤'), 'error')
    }
  }

  const handleBatchDelete = async () => {
    if (!window.confirm(`確定刪除選取的 ${selected.length} 筆商品？`)) return
    setDeletedIds(prev => new Set([...prev, ...selected]))
    try {
      const { data: deleted, error } = await supabase
        .from('products').delete().in('id', selected).select('id')
      if (error) {
        setDeletedIds(prev => { const n = new Set(prev); selected.forEach(id => n.delete(id)); return n })
        const isFK = error.code === '23503' || error.message?.toLowerCase().includes('foreign key')
        setDeleteError(isFK
          ? `部分商品有關聯紀錄，無法刪除。請個別確認後再試，或改用「停用」功能。`
          : `批量刪除失敗：${error.message || '未知錯誤'}`)
        return
      }
      const actualCount = deleted?.length || 0
      if (actualCount === 0) {
        setDeletedIds(prev => { const n = new Set(prev); selected.forEach(id => n.delete(id)); return n })
        setDeleteError('批量刪除失敗：沒有足夠的權限，或商品不存在。')
        return
      }
      try { await addLog('批量刪除商品', `刪除 ${actualCount} 筆商品`) } catch (_) {}
      showToast(`✓ 已刪除 ${actualCount} 筆`)
      setSelected([])
      onRefresh()
    } catch (err) {
      setDeletedIds(prev => { const n = new Set(prev); selected.forEach(id => n.delete(id)); return n })
      showToast('批量刪除失敗：' + (err.message || '未知錯誤'), 'error')
    }
  }

  const handleToggleActive = async (id, current) => {
    const { error } = await supabase.from('products').update({ is_active: !current, updated_at: new Date().toISOString() }).eq('id', id)
    if (error) { showToast('操作失敗：' + error.message, 'error'); return }
    showToast(`✓ 已${!current ? '啟用' : '停用'}`)
    onRefresh()
  }

  const handleQuickAdjustOpen = (p, delta) => {
    setQuickAdjust({ product: p, delta })
    setAdjustNote('')
  }

  const handleQuickAdjustConfirm = async () => {
    if (!quickAdjust) return
    const { product, delta } = quickAdjust
    if (delta > 0 && !adjustNote.trim()) { showToast('請填寫入庫備注', 'error'); return }
    if (delta < 0 && product.stock === 0) { showToast('庫存已為 0，無法出庫', 'error'); return }
    // actualDelta: cap outbound by available stock to avoid negative records
    const actualDelta = delta < 0 ? Math.max(delta, -product.stock) : delta
    const newStock = product.stock + actualDelta
    setSaving(true)
    try {
      const [{ error: e1 }, { error: e2 }] = await Promise.all([
        supabase.from('products').update({ stock: newStock, updated_at: new Date().toISOString() }).eq('id', product.id),
        supabase.from('records').insert({ product_id: product.id, type: actualDelta > 0 ? 'in' : 'out', qty: Math.abs(actualDelta), note: adjustNote || '快速調整', operator: userProfile?.name, company_id: userProfile?.company_id || null }),
      ])
      if (e1 || e2) { showToast('調整失敗：' + (e1?.message || e2?.message), 'error'); setSaving(false); return }
      try { await addLog(actualDelta > 0 ? '快速入庫' : '快速出庫', `${product.name} ${actualDelta > 0 ? '+' : ''}${actualDelta} ${product.unit}`) } catch (_) {}
      showToast(`✓ 庫存已調整：${product.stock} → ${newStock} ${product.unit}`)
      setSaving(false)
      setQuickAdjust(null)
      onRefresh()
    } catch (err) {
      showToast('調整失敗：' + (err.message || '未知錯誤'), 'error')
      setSaving(false)
    }
  }

  // CSV Export
  const handleExport = () => {
    const headers = ['商品名稱', 'SKU', '分類', '庫存', '安全庫存', '單位', '成本價', '售價', '規格', '條碼', '最小訂購量', '前置天數', '狀態']
    const rows = filtered.map(p => [
      p.name, p.sku, p.category || '', p.stock, p.safe_stock, p.unit || '',
      p.cost_price || 0, p.sell_price || 0, p.spec || '', p.barcode || '',
      p.min_order_qty || 1, p.lead_time || 0, p.is_active !== false ? '啟用' : '停用'
    ])
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url
    a.download = `YPFOPS_商品清單_${new Date().toISOString().slice(0,10)}.csv`; a.click()
    URL.revokeObjectURL(url)
  }

  // ── 下載 .xlsx 範本（原生 Excel 格式，完全相容 Win7/10/11 各版本）──
  const downloadTemplate = () => {
    const headers = ['商品名稱', 'SKU編號', '分類', '初始庫存', '安全庫存', '單位', '成本價', '售價', '規格', '條碼']
    const example = [
      ['範例商品A', 'SKU-001', '原料', 100, 20, '個', 50, 100, '規格說明', '1234567890'],
      ['範例商品B', 'SKU-002', '成品', 50,  10, '箱', 200, 350, '', ''],
    ]
    const ws = XLSX.utils.aoa_to_sheet([headers, ...example])

    // 欄位寬度（提升可讀性）
    ws['!cols'] = [
      { wch: 18 }, { wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 10 },
      { wch: 8  }, { wch: 10 }, { wch: 10 }, { wch: 18 }, { wch: 16 },
    ]

    // 標題列樣式（粗體 + 背景色）
    headers.forEach((_, ci) => {
      const addr = XLSX.utils.encode_cell({ r: 0, c: ci })
      if (!ws[addr]) return
      ws[addr].s = { font: { bold: true }, fill: { fgColor: { rgb: 'DBEAFE' } }, alignment: { horizontal: 'center' } }
    })

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, '商品匯入')

    // 備註工作表（說明欄位填寫方式）
    const noteData = [
      ['欄位', '說明', '必填', '範例'],
      ['商品名稱', '商品的完整名稱', '✅ 必填', '不鏽鋼螺絲'],
      ['SKU編號',  '唯一識別碼，同一公司不可重複', '✅ 必填', 'SKU-001'],
      ['分類',     '商品分類，可自訂', '選填', '原料'],
      ['初始庫存', '建立時的初始數量', '選填（預設0）', '100'],
      ['安全庫存', '低於此數量觸發警示', '選填（預設0）', '20'],
      ['單位',     '數量單位', '選填（預設：個）', '個、箱、kg'],
      ['成本價',   '進貨成本（NT$）', '選填（預設0）', '50'],
      ['售價',     '對外售價（NT$）', '選填（預設0）', '100'],
      ['規格',     '顏色、尺寸等補充說明', '選填', 'M號 / 紅色'],
      ['條碼',     '條碼或 QR Code 數字', '選填', '4901234567890'],
    ]
    const wsNote = XLSX.utils.aoa_to_sheet(noteData)
    wsNote['!cols'] = [{ wch: 12 }, { wch: 30 }, { wch: 18 }, { wch: 20 }]
    XLSX.utils.book_append_sheet(wb, wsNote, '欄位說明')

    XLSX.writeFile(wb, 'YPFOPS_商品匯入範本.xlsx')
  }

  // ── 上傳解析：同時支援 .xlsx / .xls / .csv，完全相容各版本 Excel ──
  const handleFileChange = (e) => {
    const file = e.target.files[0]; if (!file) return
    const reader = new FileReader()
    reader.onload = ev => {
      try {
        const data = new Uint8Array(ev.target.result)
        const wb = XLSX.read(data, { type: 'array' })
        const ws = wb.Sheets[wb.SheetNames[0]]
        // header:1 → 回傳二維陣列，第 0 列是標題
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' })

        const errors = [], preview = []
        const existingSkus = new Set(products.map(p => p.sku))

        rows.forEach((cols, i) => {
          if (i === 0) return // 跳過標題列
          // 過濾整列空白
          if (cols.every(c => String(c).trim() === '')) return

          const str = v => String(v ?? '').trim()
          const num = v => { const n = parseFloat(String(v).replace(/[^0-9.-]/g, '')); return isNaN(n) ? 0 : n }

          const name = str(cols[0])
          const sku  = str(cols[1])
          if (!name || !sku) { errors.push(`第 ${i + 1} 列：「商品名稱」或「SKU編號」為空`); return }
          if (existingSkus.has(sku)) { errors.push(`第 ${i + 1} 列：SKU「${sku}」已存在，跳過`); return }

          preview.push({
            name, sku,
            category:   str(cols[2]),
            stock:      Math.round(num(cols[3])),
            safe_stock: Math.round(num(cols[4])),
            unit:       str(cols[5]) || '個',
            cost_price: num(cols[6]),
            sell_price: num(cols[7]),
            spec:       str(cols[8]),
            barcode:    str(cols[9]),
            is_active: true,
          })
        })
        setImportErrors(errors); setImportPreview(preview)
      } catch (err) {
        setImportErrors([`無法解析檔案：${err.message}，請確認使用系統提供的範本`])
        setImportPreview([])
      }
    }
    reader.readAsArrayBuffer(file) // 二進制讀取，xlsx/xls/csv 通用
  }

  const handleImport = async () => {
    if (importPreview.length === 0) { showToast('沒有可匯入的資料', 'error'); return }
    setImporting(true)
    const cid = userProfile?.company_id || null
    const { error } = await supabase.from('products').insert(importPreview.map(p => ({ ...p, company_id: cid })))
    setImporting(false)
    if (error) { showToast('匯入失敗：' + error.message, 'error'); return }
    await addLog('批量匯入', `匯入 ${importPreview.length} 筆商品`)
    setImportPreview([]); setImportErrors([]); setShowImport(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
    showToast(`✓ 成功匯入 ${importPreview.length} 筆商品`)
    onRefresh()
  }

  const TABS = [
    { key: 'list', label: _('productListTab'), en: 'Products' },
    { key: 'analytics', label: _('productAnalyticsTab'), en: 'Analytics' },
  ]

  return (
    <div>
      <PageHeader title={_('productMgmtTitle')} en="Product Management" vi="Sản phẩm" subtitle={_('productSubtitle')} />
      {showQRPrint && <QRPrintModal products={products} onClose={() => setShowQRPrint(false)} />}

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 16 }}>
        {[
          { icon: '📦', label: _('productTotal'), value: displayProducts.length, color: C.accent, bg: C.accentDim },
          { icon: '✅', label: _('productActive'), value: activeCount, color: C.green, bg: C.greenBg },
          { icon: '⚠️', label: '低庫存', value: lowStockCount, color: C.red, bg: C.redBg, clickStatus: 'low' },
          { icon: '📂', label: _('productCategories'), value: categoryCount, color: '#7c3aed', bg: '#ede9fe' },
        ].map((k, i) => (
          <div key={i}
            onClick={k.clickStatus ? () => { setFilterStatus(k.clickStatus); setActiveTab('list'); setPage(1) } : undefined}
            style={{ background: filterStatus === k.clickStatus ? k.color + '22' : k.bg, border: `2px solid ${filterStatus === k.clickStatus ? k.color : k.color + '33'}`, borderRadius: 14, padding: '14px 16px', cursor: k.clickStatus ? 'pointer' : 'default', transition: 'all 0.15s' }}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>{k.icon}</div>
            <div style={{ fontSize: 26, fontWeight: 900, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: 12, color: C.sub, fontWeight: 600, marginTop: 2 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Total Value Banner */}
      {totalValue > 0 && (
        <div style={{ background: C.accentDim, border: `1.5px solid ${C.accent}33`, borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: C.muted, fontWeight: 600 }}>{_('productStockValue')}</span>
          <span style={{ fontSize: 18, fontWeight: 900, color: C.accent }}>NT$ {totalValue.toLocaleString()}</span>
        </div>
      )}

      {/* Low stock alert */}
      {lowStockCount > 0 && filterStatus !== 'low' && (
        <div style={{ background: C.redBg, border: `2px solid ${C.redBorder}`, borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => { setFilterStatus('low'); setActiveTab('list'); setPage(1) }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.red }}>⚠️ {lowStockCount} 項商品低於安全庫存，需要補貨</div>
            <div style={{ fontSize: 12, color: C.red, opacity: 0.8, marginTop: 2 }}>點擊查看 →</div>
          </div>
        </div>
      )}

      {/* Tab nav */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.key} style={{ ...st.btn(activeTab === t.key ? 'primary' : 'default'), padding: '10px 18px' }} onClick={() => setActiveTab(t.key)}>
            <span>{t.label}</span>
            <span style={{ fontSize: 9, opacity: 0.45, display: 'block' }}>{t.en}</span>
          </button>
        ))}
      </div>

      {/* ── LIST TAB ── */}
      {activeTab === 'list' && (
        <>
          {/* Category Chips */}
          {existingCategories.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {['all', ...existingCategories, '未分類'].map(c => {
                const isActive = filterCat === (c === 'all' ? 'all' : c)
                const count = c === 'all' ? displayProducts.length : displayProducts.filter(p => (p.category || '未分類') === c).length
                return (
                  <button key={c} onClick={() => { setFilterCat(c === 'all' ? 'all' : c); setPage(1) }}
                    style={{ padding: '5px 12px', borderRadius: 20, border: `1.5px solid ${isActive ? C.accent : C.border}`, background: isActive ? C.accentDim : '#fff', color: isActive ? C.accent : C.text, fontSize: 12, fontWeight: isActive ? 700 : 400, cursor: 'pointer', transition: 'all 0.15s' }}>
                    {c === 'all' ? `全部 (${count})` : `${c} (${count})`}
                  </button>
                )
              })}
            </div>
          )}

          {/* Filters + Actions */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input placeholder={_('productSearchPh')} value={search} onChange={e => { setSearch(e.target.value); setPage(1) }} style={{ ...st.input, width: 220 }} />
              <select style={{ ...st.input, width: 130 }} value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1) }}>
                <option value="all">{_('filterAll')}</option>
                <option value="active">{_('productActiveOpt')}</option>
                <option value="inactive">{_('productInactiveOpt')}</option>
                <option value="low">{_('productLowStockOpt')}</option>
              </select>
              {hasFilter && <button style={{ ...st.btn('default'), padding: '8px 12px', fontSize: 12 }} onClick={() => { setSearch(''); setFilterCat('all'); setFilterStatus('all'); setPage(1) }}>✕ 清除篩選</button>}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* View toggle — desktop only */}
              {!isMobile && (
                <div style={{ display: 'flex', border: `1.5px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
                  {[['card', '⊞'], ['table', '☰']].map(([v, icon]) => (
                    <button key={v} onClick={() => { setViewMode(v); setPage(1) }} style={{ padding: '8px 12px', background: viewMode === v ? C.accent : '#fff', color: viewMode === v ? '#fff' : C.muted, border: 'none', cursor: 'pointer', fontSize: 14, transition: 'all 0.15s' }}>{icon}</button>
                  ))}
                </div>
              )}
              {canDelete && selected.length > 0 && (
                <button style={{ ...st.btn('danger'), padding: '10px 14px' }} onClick={handleBatchDelete}>🗑 刪除 ({selected.length})</button>
              )}
              {canEdit && (
                <>
                  <button style={{ ...st.btn('default'), padding: '10px 14px' }} onClick={() => setShowQRPrint(true)}>{_('productQrLabels')}</button>
                  <button style={{ ...st.btn('default'), padding: '10px 14px' }} onClick={handleExport}>{_('exportBtn')}</button>
                  <button style={{ ...st.btn('default'), padding: '10px 14px' }} onClick={() => { setShowImport(!showImport); setShowAdd(false) }}>{_('importBtn')}</button>
                  <button style={st.btn()} onClick={() => { setShowAdd(!showAdd); setShowImport(false); setEditing(null); setForm(emptyForm()) }}>
                    {showAdd ? '✕ ' + _('cancel') : _('productNewProduct')}
                  </button>
                </>
              )}
            </div>
          </div>

          <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, paddingLeft: 4 }}>
            {hasFilter ? `篩選結果：${filtered.length} 項（共 ${displayProducts.length} 項）` : `共 ${displayProducts.length} 項商品`}
          </div>

          {/* Import */}
          {showImport && (
            <div style={{ ...st.card, marginBottom: 16, border: `2px solid #2a5aa0` }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>{_('productImportTitle')}</div>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>① 下載範本（.xlsx）→ ② 填入資料後直接存檔 → ③ 上傳匯入</div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>支援格式：<strong>.xlsx</strong>（建議）、.xls、.csv &nbsp;｜&nbsp; 欄位：商品名稱、SKU、分類、庫存、安全庫存、單位、成本價、售價、規格、條碼</div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                <button style={{ ...st.btn('default'), padding: '8px 16px' }} onClick={downloadTemplate}>{_('productDownloadTemplate')}</button>
                <label style={{ ...st.btn(), padding: '8px 16px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span>{_('productSelectFile')}</span>
                  <span style={{ fontSize: 9, opacity: 0.6 }}>.xlsx / .xls / .csv</span>
                  <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleFileChange} />
                </label>
              </div>
              {importErrors.length > 0 && (
                <div style={{ background: C.redBg, border: `1px solid ${C.redBorder}`, borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
                  {importErrors.map((e, i) => <div key={i} style={{ fontSize: 12, color: C.red }}>{e}</div>)}
                </div>
              )}
              {importPreview.length > 0 && (
                <div>
                  <div style={{ fontSize: 13, color: C.green, fontWeight: 700, marginBottom: 10 }}>✓ 預覽 {importPreview.length} 筆（重複 SKU 已排除）</div>
                  <div style={{ overflowX: 'auto', marginBottom: 14 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500, fontSize: 12 }}>
                      <thead><tr>{['商品名稱','SKU','分類','庫存','單位','成本','售價'].map(h => <th key={h} style={{ ...st.th, fontSize: 11 }}>{h}</th>)}</tr></thead>
                      <tbody>
                        {importPreview.slice(0, 5).map((p, i) => (
                          <tr key={i}><td style={st.td}>{p.name}</td><td style={{ ...st.td, fontFamily: 'monospace' }}>{p.sku}</td><td style={st.td}>{p.category || '-'}</td><td style={st.td}>{p.stock}</td><td style={st.td}>{p.unit}</td><td style={st.td}>{p.cost_price}</td><td style={st.td}>{p.sell_price}</td></tr>
                        ))}
                        {importPreview.length > 5 && <tr><td colSpan={7} style={{ ...st.td, color: C.muted, textAlign: 'center' }}>...還有 {importPreview.length - 5} 筆</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  <button style={{ ...st.btn('success'), padding: '11px 24px' }} onClick={handleImport} disabled={importing}>
                    {importing ? '匯入中...' : `確認匯入 ${importPreview.length} 筆`}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Add Form */}
          {showAdd && !editing && (
            <div style={{ ...st.card, marginBottom: 16, border: `2px solid ${C.accent}` }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 16 }}>{_('productNewProduct')}</div>
              <ProductFormBody form={form} setForm={setForm} existingCategories={existingCategories} />
              <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                <button style={{ ...st.btn(), flex: 1, padding: '14px' }} onClick={handleSave} disabled={saving}>{saving ? _('savingBtn') : _('productConfirmAdd')}</button>
                <button style={{ ...st.btn('default'), padding: '14px 20px' }} onClick={() => setShowAdd(false)}>{_('cancel')}</button>
              </div>
            </div>
          )}

          {/* Card View */}
          {viewMode === 'card' && (
            <>
              {filtered.length === 0 ? (
                <div style={st.card}><EmptyState icon="📦" text={hasFilter ? '沒有符合條件的商品' : _('productNoData')} /></div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
                  {pagedCard.map(p => (
                    <ProductCard key={p.id} p={p} canEdit={canEdit} canDelete={canDelete}
                      onEdit={p => { setEditing(p); setForm({ ...emptyForm(), ...p }); setShowAdd(false); setShowImport(false) }}
                      onDelete={handleDelete}
                      onToggleActive={handleToggleActive}
                      onDetail={setDetailProduct}
                      onQuickAdjust={canAdjust ? handleQuickAdjustOpen : null}
                    />
                  ))}
                </div>
              )}
              {totalCardPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
                  <button style={{ ...st.btn('default'), padding: '8px 14px' }} onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}>{_('prevPage')}</button>
                  <span style={{ padding: '8px 14px', fontSize: 13, color: C.muted }}>{page} / {totalCardPages}</span>
                  <button style={{ ...st.btn('default'), padding: '8px 14px' }} onClick={() => setPage(p => Math.min(totalCardPages, p+1))} disabled={page === totalCardPages}>{_('nextPage')}</button>
                </div>
              )}
            </>
          )}

          {/* Table View */}
          {viewMode === 'table' && (
            <div style={st.card}>
              {canDelete && pagedTable.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', borderBottom: `1px solid ${C.border}`, fontSize: 13, color: C.muted }}>
                  <input type="checkbox" checked={selected.length === pagedTable.length && pagedTable.length > 0} onChange={e => setSelected(e.target.checked ? pagedTable.map(p => p.id) : [])} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                  <span>全選本頁</span>
                </div>
              )}
              <ScrollTable>
                <thead>
                  <tr>
                    {canDelete && <th style={st.th}></th>}
                    {[['商品'],['SKU'],['分類'],['規格'],['庫存'],['成本'],['售價'],['毛利'],['狀態'],['操作']].map(([zh]) => (
                      <th key={zh} style={st.th}>{zh}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedTable.length === 0 ? (
                    <tr><td colSpan={canDelete ? 11 : 10}><EmptyState icon="📦" text={hasFilter ? '沒有符合條件的商品' : _('productNoData')} /></td></tr>
                  ) : pagedTable.map(p => {
                    const margin = p.cost_price > 0 && p.sell_price > 0 ? Math.round(((p.sell_price - p.cost_price) / p.sell_price) * 100) : null
                    return (
                      <tr key={p.id} style={{ background: selected.includes(p.id) ? C.accentDim : p.is_active === false ? '#f8fafc' : 'transparent', opacity: p.is_active === false ? 0.65 : 1 }}>
                        {canDelete && (
                          <td style={st.td}><input type="checkbox" checked={selected.includes(p.id)} onChange={e => setSelected(prev => e.target.checked ? [...prev, p.id] : prev.filter(id => id !== p.id))} style={{ width: 16, height: 16, cursor: 'pointer' }} /></td>
                        )}
                        <td style={st.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {p.image_url ? (
                              <img src={p.image_url} alt={p.name} style={{ width: 34, height: 34, borderRadius: 8, objectFit: 'cover', border: `1px solid ${C.border}` }} onError={e => e.target.style.display = 'none'} />
                            ) : (
                              <div style={{ width: 34, height: 34, borderRadius: 8, background: C.accentDim, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>📦</div>
                            )}
                            <div>
                              <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{p.name}</div>
                              {p.barcode && <div style={{ fontSize: 10, color: C.muted, fontFamily: 'monospace' }}>🔖 {p.barcode}</div>}
                            </div>
                          </div>
                        </td>
                        <td style={{ ...st.td, fontFamily: 'monospace', color: C.accent, fontSize: 12 }}>{p.sku}</td>
                        <td style={st.td}>{p.category || '-'}</td>
                        <td style={{ ...st.td, color: C.muted, fontSize: 12 }}>{p.spec || '-'}</td>
                        <td style={st.td}>
                          <span style={{ fontWeight: 700, color: p.stock <= p.safe_stock ? C.red : C.green }}>{p.stock}</span>
                          <span style={{ fontSize: 11, color: C.muted }}> {p.unit}</span>
                          {p.stock <= p.safe_stock && <div style={{ fontSize: 10, color: C.red }}>⚠ 低庫存</div>}
                        </td>
                        <td style={st.td}>{p.cost_price > 0 ? `NT$${p.cost_price.toLocaleString()}` : '-'}</td>
                        <td style={st.td}>{p.sell_price > 0 ? `NT$${p.sell_price.toLocaleString()}` : '-'}</td>
                        <td style={st.td}>
                          {margin !== null ? (
                            <span style={{ color: margin >= 30 ? C.green : margin >= 15 ? C.yellow : C.red, fontWeight: 700 }}>{margin}%</span>
                          ) : '-'}
                        </td>
                        <td style={st.td}>
                          <span style={{ ...st.badge(p.is_active !== false ? 'ok' : ''), fontSize: 11 }}>{p.is_active !== false ? _('productActiveStatus') : _('productInactiveStatus')}</span>
                        </td>
                        <td style={st.td}>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <button style={{ ...st.btn('default'), padding: '4px 8px', fontSize: 11, minHeight: 32 }} onClick={() => setDetailProduct(p)}>{_('productDetail')}</button>
                            {canEdit && <button style={{ ...st.btn('warning'), padding: '4px 8px', fontSize: 11, minHeight: 32 }} onClick={() => { setEditing(p); setForm({ ...emptyForm(), ...p }); setShowAdd(false) }}>編輯</button>}
                            {canAdjust && <button style={{ ...st.btn('success'), padding: '4px 8px', fontSize: 11, minHeight: 32 }} onClick={() => handleQuickAdjustOpen(p, 1)}>入庫</button>}
                            {canAdjust && <button style={{ ...st.btn('danger'), padding: '4px 8px', fontSize: 11, minHeight: 32 }} onClick={() => handleQuickAdjustOpen(p, -1)}>出庫</button>}
                            {canDelete && <button style={{ ...st.btn('danger'), padding: '4px 8px', fontSize: 11, minHeight: 32 }} onClick={() => handleDelete(p.id, p.name)}>刪除</button>}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </ScrollTable>
              <Pagination page={page} total={totalTablePages} onChange={setPage} />
            </div>
          )}
        </>
      )}

      {/* ── ANALYTICS TAB ── */}
      {activeTab === 'analytics' && (
        <div>
          {/* KPI row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { label: '啟用商品', value: activeCount, color: C.green },
              { label: '低庫存警示', value: analytics.lowStockList.length, color: C.red },
              { label: '已停用', value: analytics.inactiveCount, color: C.muted },
              { label: '庫存總值', value: `NT$${Math.round(totalValue / 1000)}k`, color: C.accent },
              analytics.avgMargin !== null ? { label: '平均毛利率', value: `${analytics.avgMargin}%`, color: analytics.avgMargin >= 30 ? C.green : C.yellow } : null,
            ].filter(Boolean).map((k, i) => (
              <div key={i} style={{ background: '#fff', border: `2px solid ${k.color}33`, borderRadius: 14, padding: '14px 16px' }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: k.color }}>{k.value}</div>
                <div style={{ fontSize: 12, color: C.sub, marginTop: 4, fontWeight: 600 }}>{k.label}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {/* Category count */}
            <div style={st.card}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 14 }}>📂 分類商品數</div>
              {analytics.catList.length === 0 ? <EmptyState icon="📂" text="尚無分類資料" /> : (
                <RankBars data={analytics.catList.map(([k, v]) => ({ k, v: v.count }))} color={C.accent} />
              )}
            </div>

            {/* Category value */}
            <div style={st.card}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 14 }}>💰 分類庫存總值</div>
              {analytics.catList.filter(([,v]) => v.value > 0).length === 0 ? (
                <EmptyState icon="💰" text="尚無價格資料" />
              ) : (
                <ColBarChart data={analytics.catList.filter(([,v]) => v.value > 0).slice(0, 8).map(([k,v]) => ({ k, v: Math.round(v.value) }))} color={C.green} unit="NT$" />
              )}
            </div>

            {/* Top products by value */}
            <div style={st.card}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 14 }}>🏆 庫存總值 Top 8</div>
              {analytics.byValueTop.length === 0 ? <EmptyState icon="🏆" text="尚無價格資料" /> : (
                <RankBars data={analytics.byValueTop.map(p => ({ k: p.name, v: Math.round(p.stock * p.cost_price) }))} color={C.accent} unit="NT$" />
              )}
            </div>

            {/* Low stock list */}
            <div style={st.card}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 14 }}>⚠️ 低庫存商品（需補貨）</div>
              {analytics.lowStockList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0', color: C.green, fontSize: 14, fontWeight: 700 }}>✅ 所有商品庫存充足！</div>
              ) : analytics.lowStockList.map(p => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', marginBottom: 6, background: C.redBg, borderRadius: 10, border: `1.5px solid ${C.redBorder}` }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{p.sku} · {p.category || '未分類'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 16, fontWeight: 900, color: C.red }}>{p.stock} <span style={{ fontSize: 11, fontWeight: 400 }}>{p.unit}</span></div>
                    <div style={{ fontSize: 10, color: C.muted }}>安全: {p.safe_stock}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Category low stock count */}
            {analytics.catList.some(([,v]) => v.lowStock > 0) && (
              <div style={st.card}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 14 }}>📉 各分類低庫存數量</div>
                <RankBars data={analytics.catList.filter(([,v]) => v.lowStock > 0).map(([k,v]) => ({ k, v: v.lowStock }))} color={C.red} />
              </div>
            )}

            {/* Export */}
            <div style={{ ...st.card, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>📤 匯出報表</div>
              <div style={{ fontSize: 12, color: C.muted }}>匯出當前篩選的商品清單為 CSV，可直接用 Excel 開啟</div>
              <button style={{ ...st.btn('success'), padding: '12px 20px' }} onClick={handleExport}>
                📤 匯出商品清單 CSV
              </button>
              {lowStockCount > 0 && (
                <button style={{ ...st.btn('danger'), padding: '12px 20px' }} onClick={() => {
                  const headers = ['商品名稱', 'SKU', '分類', '現有庫存', '安全庫存', '單位', '缺口']
                  const rows = analytics.lowStockList.map(p => [p.name, p.sku, p.category || '-', p.stock, p.safe_stock, p.unit || '', p.safe_stock - p.stock])
                  const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n')
                  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a'); a.href = url; a.download = `YPFOPS_低庫存清單_${new Date().toISOString().slice(0,10)}.csv`; a.click()
                }}>
                  ⚠️ 匯出低庫存清單
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Error Modal */}
      {deleteError && (
        <Modal title="⚠️ 無法刪除" onClose={() => setDeleteError(null)}>
          <div style={{ padding: '16px', background: C.redBg, border: `1.5px solid ${C.redBorder}`, borderRadius: 12, marginBottom: 20 }}>
            {deleteError.split('\n').map((line, i) => (
              <div key={i} style={{ fontSize: 14, color: C.red, fontWeight: i === 0 ? 700 : 400, lineHeight: 1.6 }}>{line}</div>
            ))}
          </div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
            💡 <strong>建議：</strong>若商品有歷史紀錄需要保留，請改用「停用」功能，停用後商品不會出現在選單中，歷史記錄也會完整保留。
          </div>
          <button style={{ ...st.btn('default'), width: '100%', padding: '12px' }} onClick={() => setDeleteError(null)}>
            我知道了
          </button>
        </Modal>
      )}

      {/* Edit Modal */}
      {editing && (
        <Modal title={`✏️ 編輯商品 — ${editing.name}`} onClose={() => { setEditing(null); setShowAdd(false) }}>
          <ProductFormBody form={form} setForm={setForm} existingCategories={existingCategories} />
          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <button style={{ ...st.btn(), flex: 1, padding: '14px' }} onClick={handleSave} disabled={saving}>{saving ? _('savingBtn') : _('productConfirmUpdate')}</button>
            <button style={{ ...st.btn('default'), padding: '14px 20px' }} onClick={() => { setEditing(null); setShowAdd(false) }}>{_('cancel')}</button>
          </div>
        </Modal>
      )}

      {/* Quick Adjust Modal */}
      {quickAdjust && (
        <Modal title={`${quickAdjust.delta > 0 ? _('productStockInTitle') : _('productStockOutTitle')} — ${quickAdjust.product.name}`} onClose={() => setQuickAdjust(null)}>
          <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: 10, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: C.muted }}>目前庫存</span>
            <span style={{ fontSize: 20, fontWeight: 900, color: C.accent }}>{quickAdjust.product.stock} {quickAdjust.product.unit}</span>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={st.label}>調整數量</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button style={{ ...st.btn('danger'), padding: '8px 16px', fontSize: 16 }}
                onClick={() => setQuickAdjust(q => {
                  // Decrease magnitude (min 1)
                  const newMag = Math.max(1, Math.abs(q.delta) - 1)
                  return { ...q, delta: q.delta > 0 ? newMag : -newMag }
                })}>－</button>
              <input type="number" style={{ ...st.input, flex: 1, textAlign: 'center', fontSize: 18, fontWeight: 900 }} value={Math.abs(quickAdjust.delta)} min="1"
                onChange={e => {
                  const v = Math.max(1, parseInt(e.target.value) || 1)
                  setQuickAdjust(q => ({ ...q, delta: q.delta > 0 ? v : -v }))
                }} />
              <button style={{ ...st.btn('success'), padding: '8px 16px', fontSize: 16 }}
                onClick={() => setQuickAdjust(q => {
                  // Increase magnitude
                  const newMag = Math.abs(q.delta) + 1
                  return { ...q, delta: q.delta > 0 ? newMag : -newMag }
                })}>＋</button>
            </div>
            {quickAdjust.delta < 0 && Math.abs(quickAdjust.delta) > quickAdjust.product.stock && (
              <div style={{ marginTop: 6, fontSize: 12, color: C.yellow, fontWeight: 600 }}>
                ⚠ 出庫量超過庫存，將以實際庫存 {quickAdjust.product.stock} {quickAdjust.product.unit} 為準
              </div>
            )}
            <div style={{ marginTop: 8, fontSize: 13, color: C.muted, textAlign: 'center' }}>
              操作後庫存：<strong style={{ color: C.green }}>
                {Math.max(0, quickAdjust.product.stock + quickAdjust.delta)} {quickAdjust.product.unit}
              </strong>
            </div>
          </div>
          <label style={st.label}>備注 {quickAdjust.delta > 0 && '*'}</label>
          <input style={{ ...st.input, marginBottom: 16 }} value={adjustNote} placeholder={quickAdjust.delta > 0 ? '請填寫入庫原因（必填）' : '說明（選填）'} onChange={e => setAdjustNote(e.target.value)} />
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={{ ...st.btn(quickAdjust.delta > 0 ? 'success' : 'danger'), flex: 1, padding: '14px' }} onClick={handleQuickAdjustConfirm} disabled={saving}>
              {saving ? '處理中...' : quickAdjust.delta > 0 ? `▲ 確認入庫 +${Math.abs(quickAdjust.delta)}` : `▼ 確認出庫 -${Math.abs(quickAdjust.delta)}`}
            </button>
            <button style={{ ...st.btn('default'), padding: '14px 20px' }} onClick={() => setQuickAdjust(null)}>{_('cancel')}</button>
          </div>
        </Modal>
      )}

      {/* Detail Modal */}
      {detailProduct && (
        <Modal title={`${_('productDetailTitle')} — ${detailProduct.name}`} onClose={() => setDetailProduct(null)}>
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            {detailProduct.image_url && (
              <img src={detailProduct.image_url} alt={detailProduct.name} style={{ width: 120, height: 120, borderRadius: 12, objectFit: 'cover', border: `2px solid ${C.border}` }} onError={e => e.target.style.display = 'none'} />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 4 }}>{detailProduct.name}</div>
              <div style={{ fontSize: 13, fontFamily: 'monospace', color: C.accent }}>{detailProduct.sku}</div>
              {detailProduct.spec && <div style={{ fontSize: 13, color: C.sub, marginTop: 6 }}>{detailProduct.spec}</div>}
              <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ ...st.badge(detailProduct.is_active !== false ? 'ok' : ''), fontSize: 12 }}>{detailProduct.is_active !== false ? _('productActiveOpt') : _('productInactiveOpt')}</span>
                {detailProduct.stock <= detailProduct.safe_stock && <span style={{ ...st.badge('warn'), fontSize: 12 }}>⚠ 低庫存</span>}
              </div>
            </div>
          </div>

          {detailProduct.safe_stock > 0 && (
            <div style={{ marginBottom: 16, padding: '12px 14px', background: '#f8fafc', borderRadius: 10, border: `1.5px solid ${C.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.muted, marginBottom: 6 }}>
                <span>庫存水位</span>
                <span>{detailProduct.stock} / 安全庫存 {detailProduct.safe_stock} {detailProduct.unit}</span>
              </div>
              <div style={{ height: 8, background: C.border, borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(100, (detailProduct.stock / Math.max(detailProduct.safe_stock * 2, 1)) * 100)}%`, background: detailProduct.stock <= detailProduct.safe_stock ? C.red : C.green, borderRadius: 4 }} />
              </div>
            </div>
          )}

          {/* Profit margin if available */}
          {detailProduct.cost_price > 0 && detailProduct.sell_price > 0 && (
            <div style={{ marginBottom: 16, padding: '12px 16px', background: C.greenBg, borderRadius: 10, border: `1.5px solid ${C.greenBorder}`, display: 'flex', justifyContent: 'space-around' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: C.muted }}>成本價</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>NT${detailProduct.cost_price.toLocaleString()}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: C.muted }}>售價</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.accent }}>NT${detailProduct.sell_price.toLocaleString()}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: C.muted }}>毛利率</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.green }}>
                  {Math.round(((detailProduct.sell_price - detailProduct.cost_price) / detailProduct.sell_price) * 100)}%
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
            {[
              ['分類', detailProduct.category || '-'],
              ['單位', detailProduct.unit || '-'],
              ['條碼', detailProduct.barcode || '-'],
              ['重量', detailProduct.weight ? `${detailProduct.weight} kg` : '-'],
              [_('productCurrentStock'), `${detailProduct.stock} ${detailProduct.unit || ''}`],
              ['安全庫存', `${detailProduct.safe_stock} ${detailProduct.unit || ''}`],
              ['最小訂購量', `${detailProduct.min_order_qty || 1} ${detailProduct.unit || ''}`],
              ['前置時間', detailProduct.lead_time ? `${detailProduct.lead_time} 天` : '-'],
              ['庫存總值', detailProduct.cost_price > 0 ? `NT$ ${(detailProduct.stock * detailProduct.cost_price).toLocaleString()}` : '-'],
              ['建立時間', formatDate(detailProduct.created_at)],
            ].map(([label, value]) => (
              <div key={label} style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: 10, border: `1.5px solid ${C.border}` }}>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 4, fontWeight: 600 }}>{label}</div>
                <div style={{ fontSize: 15, color: C.text, fontWeight: 700 }}>{value}</div>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  )
}
